import { ALL_METRICS_OPTIONS, getCohortFieldAndPeriod } from './metricsUtils';
import { getFixedNumber } from './components/creativeCardData/metricDisplayUtils';

const PERCENTAGE_DISPLAY_TYPE = 'percentage';

const OPERATORS_KEY_PREFIX = 'STATIC.PAGES.CREATIVE_GALLERY.METRIC_FILTER.OPERATORS';

export const MetricFilterOperators = {
    GT: 'gt',
    GTE: 'gte',
    LT: 'lt',
    LTE: 'lte',
    BETWEEN: 'between',
    EQ: 'eq',
    NEQ: 'neq',
};

export const METRIC_FILTER_OPERATOR_OPTIONS = [
    { value: MetricFilterOperators.GT, label: `${OPERATORS_KEY_PREFIX}.GT`, symbol: '>' },
    { value: MetricFilterOperators.GTE, label: `${OPERATORS_KEY_PREFIX}.GTE`, symbol: '≥' },
    { value: MetricFilterOperators.LT, label: `${OPERATORS_KEY_PREFIX}.LT`, symbol: '<' },
    { value: MetricFilterOperators.LTE, label: `${OPERATORS_KEY_PREFIX}.LTE`, symbol: '≤' },
    { value: MetricFilterOperators.BETWEEN, label: `${OPERATORS_KEY_PREFIX}.BETWEEN`, symbol: null },
    { value: MetricFilterOperators.EQ, label: `${OPERATORS_KEY_PREFIX}.EQ`, symbol: '=' },
    { value: MetricFilterOperators.NEQ, label: `${OPERATORS_KEY_PREFIX}.NEQ`, symbol: '≠' },
];

const OPERATOR_SYMBOL_BY_VALUE = METRIC_FILTER_OPERATOR_OPTIONS.reduce((acc, { value, symbol }) => {
    acc[value] = symbol;
    return acc;
}, {});

export const getOperatorSymbol = operator => OPERATOR_SYMBOL_BY_VALUE[operator] ?? null;

// Reuses the card formatter so chips and cards agree on locale and precision.
export const formatMetricFilterNumber = value => getFixedNumber(Number(value));

export const formatMetricFilterValue = (value, isPercentage = false) =>
    isPercentage ? `${formatMetricFilterNumber(value)}%` : formatMetricFilterNumber(value);

// Percentage metrics (CTR, ROI, ...) are stored as fractions but entered/displayed as 0-100.
// The format is resolved from a cohort option, a metric key, or a cohort's base metric key.
export const isPercentageField = (field, fieldsMetadata = {}) => {
    const format =
        field.display_format ||
        fieldsMetadata[field.value]?.display_format ||
        fieldsMetadata[getCohortFieldAndPeriod(field.value)[0]]?.display_format;
    return format?.type === PERCENTAGE_DISPLAY_TYPE;
};

const hasValue = value => value !== '' && value !== null && value !== undefined;

const VALID_OPERATORS = new Set(Object.values(MetricFilterOperators));

export const createEmptyCondition = (id, field = '') => ({
    id,
    field,
    operator: MetricFilterOperators.GTE,
    value: '',
    min: '',
    max: '',
});

export const isConditionComplete = ({ field, operator, value, min, max }) => {
    if (!field || !VALID_OPERATORS.has(operator)) return false;
    if (operator === MetricFilterOperators.BETWEEN) return hasValue(min) && hasValue(max);
    return hasValue(value);
};

export const countActiveConditions = conditions => conditions.filter(isConditionComplete).length;

// A reversed between range (min > max) matches nothing, so swap the bounds to honor the user's intent.
export const normalizeConditionBounds = condition => {
    if (condition.operator !== MetricFilterOperators.BETWEEN || !isConditionComplete(condition)) return condition;
    return Number(condition.min) > Number(condition.max)
        ? { ...condition, min: condition.max, max: condition.min }
        : condition;
};

const METRIC_FIELD_VALUES = new Set(ALL_METRICS_OPTIONS.map(({ value }) => value));
const isMetricField = field => METRIC_FIELD_VALUES.has(field);

// Metric values live on creative.metrics, cohort values on creative.cohorts (mirrors Sort By).
const getCreativeValue = (creative, field) => (isMetricField(field) ? creative.metrics : creative.cohorts)?.[field];

// Tolerant equality: the percentage /100 conversion introduces float artifacts
// (e.g. 7.1 / 100 === 0.07100000000000001), so strict === would miss exact matches.
const EQUALITY_EPSILON = 1e-9;
const approxEquals = (a, b) => Math.abs(a - b) <= EQUALITY_EPSILON * Math.max(1, Math.abs(a), Math.abs(b));

export const conditionMatchesCreative = (creative, { field, operator, value, min, max }, isPercentage = false) => {
    const creativeValue = getCreativeValue(creative, field);
    // A missing value (lazy metrics still loading, or genuinely no data - shown as N/A) is not 0:
    // coalescing would make conditions like "ROI <= 0" match creatives that simply have no data.
    if (creativeValue === null || creativeValue === undefined) return false;
    // A percentage threshold is entered as 0-100 but compared against the stored fraction.
    const threshold = raw => (isPercentage ? Number(raw) / 100 : Number(raw));

    switch (operator) {
        case MetricFilterOperators.GT:
            return creativeValue > threshold(value);
        case MetricFilterOperators.GTE:
            return creativeValue >= threshold(value);
        case MetricFilterOperators.LT:
            return creativeValue < threshold(value);
        case MetricFilterOperators.LTE:
            return creativeValue <= threshold(value);
        case MetricFilterOperators.EQ:
            return approxEquals(creativeValue, threshold(value));
        case MetricFilterOperators.NEQ:
            return !approxEquals(creativeValue, threshold(value));
        case MetricFilterOperators.BETWEEN:
            return creativeValue >= threshold(min) && creativeValue <= threshold(max);
        default:
            return false;
    }
};

export const filterCreativesByMetricConditions = (creatives, conditions, percentageFields = new Set()) => {
    // Conditions applied through the popover are already normalized, but URL-restored or
    // hand-edited ones may carry a reversed between range; keep the matcher order-independent.
    const activeConditions = conditions.filter(isConditionComplete).map(normalizeConditionBounds);
    if (!activeConditions.length) return creatives;

    return creatives.filter(creative =>
        activeConditions.every(condition =>
            conditionMatchesCreative(creative, condition, percentageFields.has(condition.field))
        )
    );
};

// Blank-field rows are kept so a not-yet-configured row doesn't disappear mid-edit.
export const pruneConditionsToFields = (conditions, allowedFields) => {
    const allowed = new Set(allowedFields);
    return conditions.filter(({ field }) => !field || allowed.has(field));
};
